"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { useScrollProgress } from "./film-motion";
import { getLitAmount } from "./film.utils";

type LitStatementProps = {
  text: string;
  className?: string;
};

/**
 * A statement whose characters light up as it scrolls through the viewport.
 * One CSS variable per frame drives every character. Fully lit without JS and
 * with reduced motion.
 */
export const LitStatement = ({ text, className }: LitStatementProps) => {
  const ref = useRef<HTMLHeadingElement>(null);
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Only dim text that is still below the fold, so nothing visible goes dark.
    if (element.getBoundingClientRect().top < window.innerHeight * 0.8) return;
    setArmed(true);
  }, []);

  const onProgress = useCallback((progress: number) => {
    ref.current?.style.setProperty("--lit", String(getLitAmount(progress)));
  }, []);
  useScrollProgress(ref, onProgress, "through", armed);

  let index = 0;
  const words = text.split(" ");

  return (
    <h2
      ref={ref}
      aria-label={text}
      data-armed={armed ? "" : undefined}
      className={`film-lit ${className ?? ""}`}
      style={{ "--n": text.length } as CSSProperties}
    >
      <span aria-hidden>
        {words.map((word, wordIndex) => (
          <span key={wordIndex}>
            <span className="inline-block whitespace-nowrap">
              {[...word].map((character) => {
                const i = index++;
                return (
                  <span key={i} data-char style={{ "--i": i } as CSSProperties}>
                    {character}
                  </span>
                );
              })}
            </span>
            {wordIndex < words.length - 1 ? " " : null}
          </span>
        ))}
      </span>
    </h2>
  );
};
